if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      monoreeVariantRequestId = 0;
      monoreeVariantState = 'ready';
      monoreeVariantPurchasable = false;
      monoreeCommittedVariantId = null;
      monoreeSelectedVariant = null;
      monoreeVariantRequest = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.monoreeSizeChosen = this.querySelector('variant-selects')?.dataset.monoreeSizeRequired !== 'true';
        const initialVariant = this.getSelectedVariant(this);
        this.monoreeSelectedVariant = initialVariant ?? null;
        this.monoreeCommittedVariantId = initialVariant?.id ?? null;
        this.monoreeVariantPurchasable = initialVariant?.available === true &&
          this.productForm?.submitButton?.dataset.monoreePurchasable !== 'false';
        this.monoreeRetryHandler = (event) => {
          if (!event.target.closest('[data-monoree-variant-retry]')) return;
          event.preventDefault();
          this.retryMonoreeVariantSelection();
        };
        this.monoreePaymentGuard = (event) => {
          if (!event.target.closest('[data-monoree-accelerated]')) return;
          if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
          if (this.monoreeCanPurchase()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        };
        this.monoreeQuantityGuard = (event) => {
          if (event.target === this.quantityInput) this.applyMonoreeSizeState();
        };
        this.addEventListener('input', this.monoreeQuantityGuard);
        this.addEventListener('change', this.monoreeQuantityGuard);
        this.addEventListener('click', this.monoreePaymentGuard, true);
        this.addEventListener('keydown', this.monoreePaymentGuard, true);
        this.addEventListener('click', this.monoreeRetryHandler);
        this.applyMonoreeSizeState();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      monoreeCanPurchase() {
        return this.monoreeVariantState === 'ready' && this.monoreeSizeChosen &&
          this.monoreeVariantPurchasable && Boolean(this.monoreeCommittedVariantId) &&
          (!this.quantityInput || (Number(this.quantityInput.value) > 0 && this.quantityInput.validity.valid));
      }

      applyMonoreeSizeState() {
        const pending = !this.monoreeSizeChosen;
        const canPurchase = this.monoreeCanPurchase();
        const loading = this.monoreeVariantState === 'loading';
        const failed = this.monoreeVariantState === 'error';
        const canNotify = !pending && !loading && !failed &&
          this.monoreeSelectedVariant?.available === false && Boolean(this.monoreeSelectedVariant.id);
        this.dataset.monoreeSizePending = String(pending);
        this.dataset.monoreeVariantState = this.monoreeVariantState;
        this.querySelectorAll('product-form').forEach((form) => {
          const formCanNotify = canNotify && Boolean(form.querySelector('[data-monoree-restock]'));
          form.dataset.monoreeSizePending = String(pending);
          form.dataset.monoreeVariantPending = String(loading || failed);
          form.dataset.monoreeSelectedVariantId = formCanNotify ? String(this.monoreeSelectedVariant.id) : '';
          form.dataset.monoreeNotifyAvailable = String(formCanNotify);
          form.form?.setAttribute('aria-busy', String(loading));
          const text = pending && !loading && !failed
            ? this.variantSelectors?.dataset.monoreeSizeMessage
            : this.monoreeVariantState === 'unavailable' || (!loading && !failed && !this.monoreeVariantPurchasable)
              ? window.variantStrings.soldOut : undefined;
          form.toggleSubmitButton?.(!canPurchase, text);
          form.querySelectorAll('[data-monoree-restock]').forEach((button) => {
            button.disabled = !formCanNotify;
            button.setAttribute('aria-hidden', String(!formCanNotify));
          });
        });
        this.updateVariantInputs(canPurchase ? this.monoreeCommittedVariantId : null, !loading && !failed);
        this.querySelectorAll('[data-monoree-accelerated]').forEach((button) => {
          button.inert = !canPurchase;
          button.setAttribute('aria-disabled', String(!canPurchase));
        });
        this.querySelectorAll('[data-monoree-size-hint]').forEach((hint) => {
          hint.hidden = !pending || loading || failed;
        });
        if (pending) this.querySelectorAll('[data-monoree-size-option] input:checked').forEach((input) => {
          input.checked = false;
        });
      }

      setMonoreeVariantState(state, message) {
        this.monoreeVariantState = state;
        this.applyMonoreeSizeState();
        const form = this.productForm;
        const text = message || (state === 'loading' ? form?.dataset.monoreeLabelLoading
          : state === 'error' ? form?.dataset.monoreeLabelError
          : state === 'unavailable' ? form?.dataset.monoreeLabelUnavailable : '');
        this.querySelectorAll('[data-monoree-variant-message]').forEach((node) => {
          node.textContent = text || '';
          node.hidden = !text;
        });
        this.querySelectorAll('[data-monoree-variant-retry]').forEach((button) => {
          button.hidden = state !== 'error';
          button.disabled = state === 'loading';
        });
      }

      retryMonoreeVariantSelection() {
        if (this.monoreeVariantState !== 'error' || !this.monoreeVariantRequest) return;
        return this.renderProductInfo(this.monoreeVariantRequest);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.monoreeVariantRequestId += 1;
        this.abortController?.abort();
        this.removeEventListener('input', this.monoreeQuantityGuard);
        this.removeEventListener('change', this.monoreeQuantityGuard);
        this.removeEventListener('click', this.monoreePaymentGuard, true);
        this.removeEventListener('keydown', this.monoreePaymentGuard, true);
        this.removeEventListener('click', this.monoreeRetryHandler);
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        if (target.closest('[data-monoree-size-option]')) this.monoreeSizeChosen = true;
        this.querySelectorAll('[data-monoree-accelerated]').forEach((button) => { button.inert = true; });
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        const requestId = ++this.monoreeVariantRequestId;
        const selectionElement = this.variantSelectors;
        const previousSelection = this.monoreeSelectRequest;
        if (previousSelection?.deferred && previousSelection.deferred !== selectionElement?.pendingSelectPromise) {
          previousSelection.deferred.promise?.catch(() => {});
          previousSelection.deferred.reject(new DOMException('Selection changed', 'AbortError'));
        }
        this.monoreeSelectRequest = {
          element: selectionElement,
          deferred: selectionElement?.pendingSelectPromise,
        };
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        this.monoreeVariantRequest = { requestUrl, targetId, callback };
        this.setMonoreeVariantState('loading');
        let timeout;
        const responsePromise = fetch(requestUrl, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Variant request failed (${response.status})`);
            return response.text();
          });
        const timeoutPromise = new Promise((resolve, reject) => {
          timeout = setTimeout(() => {
            controller.abort();
            const error = new Error('Variant request timed out');
            error.name = 'TimeoutError';
            reject(error);
          }, 15000);
        });
        return Promise.race([responsePromise, timeoutPromise])
          .then((responseText) => {
            if (requestId !== this.monoreeVariantRequestId) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            if (requestId !== this.monoreeVariantRequestId) return;
            this.pendingRequestUrl = null;
            this.monoreeSelectRequest = null;
            document.getElementById(targetId)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (requestId !== this.monoreeVariantRequestId) return;
            this.pendingRequestUrl = null;
            this.setMonoreeVariantState('error');
            this.monoreeSelectRequest?.element?.rejectPendingSelectPromise(error);
            this.monoreeSelectRequest = null;
          })
          .finally(() => clearTimeout(timeout));
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        const variantSelects = this.getVariantSelects(productInfoNode);
        return variantSelects
          ? this.parseJsonScript(variantSelects, '[data-selected-variant]')
          : this.parseJsonScript(productInfoNode, '[data-monoree-initial-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const currentVariantSelects = this.variantSelectors;
          const sourceVariantSelects = this.getVariantSelects(html);
          if (!sourceVariantSelects) throw new Error('Missing variant selection in response');
          const variant = this.getSelectedVariant(html);
          const sizeGroup = sourceVariantSelects.querySelector('[data-monoree-size-option]');
          if (sizeGroup) {
            const selectedSize = sizeGroup.querySelector('input:checked, select option:checked');
            this.monoreeSizeChosen = Boolean(selectedSize?.dataset.optionValueId &&
              !selectedSize.disabled);
          }
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.pickupAvailability?.update(variant);
          this.monoreeSelectedVariant = variant ?? null;
          this.monoreeCommittedVariantId = variant?.id ?? null;
          this.monoreeVariantPurchasable = false;
          if (!variant) {
            this.setUnavailable();
            currentVariantSelects?.resolvePendingSelectPromise(null, sourceVariantSelects);
            return;
          }
          const sourceButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          if (!sourceButton || !variant.id) throw new Error('Incomplete variant response');
          this.updateMedia(html, variant.featured_media?.id);
          const updateSourceFromDestination = (id, shouldHide = () => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };
          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelectorAll('.monoree-variant-unavailable').forEach((node) => {
            node.classList.remove('monoree-variant-unavailable');
          });
          this.monoreeVariantPurchasable = variant.available === true &&
            sourceButton.dataset.monoreePurchasable === 'true';
          this.setMonoreeVariantState(this.monoreeVariantPurchasable ? 'ready' : 'unavailable');
          currentVariantSelects?.resolvePendingSelectPromise(variant, sourceVariantSelects);
          publish(PUB_SUB_EVENTS.variantChange, {
            data: { sectionId: this.sectionId, html, variant },
          });
        };
      }

      updateVariantInputs(variantId, notify = true) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          const value = variantId ? String(variantId) : '';
          const changed = input.value !== value || input.disabled === Boolean(variantId);
          input.value = value;
          input.disabled = !variantId;
          if (changed && notify) input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelectorAll('share-button, monoree-product-share').forEach((share) => {
          share.updateUrl?.(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`);
        });

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.monoreeSelectedVariant = null;
        this.monoreeCommittedVariantId = null;
        this.monoreeVariantPurchasable = false;
        this.setMonoreeVariantState('unavailable');
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`).join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => {
          classList.remove('hidden');
          classList.add('monoree-variant-unavailable');
        });
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [, , sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [, , sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          { scrollPage: false }
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max !== null) {
          this.quantityInput.max = Math.max(0, max);
        } else {
          this.quantityInput.removeAttribute('max');
        }
        const step = Math.max(1, data.step);
        const currentValue = parseInt(this.quantityInput.value, 10);
        let value = Math.max(min, Number.isFinite(currentValue) ? currentValue : min);
        value = min + Math.ceil((value - min) / step) * step;
        if (max !== null && value > max) value = min + Math.floor((max - min) / step) * step;
        const nextValue = String(Math.max(0, value));
        if (this.quantityInput.value !== nextValue) {
          this.quantityInput.value = nextValue;
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId || !this.monoreeCanPurchase()) return;
        const requestId = this.monoreeVariantRequestId;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => {
            if (!response.ok) throw new Error('Quantity request failed');
            return response.text();
          })
          .then((responseText) => {
            if (requestId !== this.monoreeVariantRequestId ||
                this.productForm?.variantIdInput?.value !== currentVariantId) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => spinner?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

